import {
    Drawer,
    DrawerOverlay,
    DrawerContent,
    Box,
    Text,
    Flex,
    Icon,
    IconButton,
    Spinner,
    Center,
} from '@chakra-ui/react';
import React from 'react';
import { TbX } from 'react-icons/tb';
import { useInventoryImportHistory } from '../../hooks/inventory/useImportInventoryHistory';
import { useForceSidebarCollapsed } from '../../contexts/SidebarCollapseOverrideContext';
import { INSTANT_SIDE_DRAWER_MOTION_PROPS } from '../sideDrawerContainerProps';

interface InventoryImportHistoryDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    onSelectJob: (jobId: string) => void;
}

const InventoryImportHistoryDrawer: React.FC<InventoryImportHistoryDrawerProps> = ({
    isOpen,
    onClose,
    onSelectJob,
}) => {
    useForceSidebarCollapsed(isOpen);
    const { data, isLoading, isError } = useInventoryImportHistory(isOpen);
    const imports = data?.imports ?? [];

    return (
        <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="md" closeOnOverlayClick>
            <DrawerOverlay />
            <DrawerContent data-partbay-drawer="" motionProps={INSTANT_SIDE_DRAWER_MOTION_PROPS}>
                <Flex direction="column" h="100%">
                    <Flex
                        align="center"
                        justify="space-between"
                        px={5}
                        h="topbar"
                        flexShrink={0}
                        borderBottom="1px solid"
                        borderColor="border.default"
                        bg="bg.subtle"
                    >
                        <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                            Import History
                        </Text>
                        <IconButton
                            aria-label="Close"
                            icon={<Icon as={TbX as React.ElementType} />}
                            variant="ghost"
                            size="sm"
                            onClick={onClose}
                        />
                    </Flex>

                    <Box flex={1} overflowY="auto" px={5} py={4} bg="white">
                        {isLoading && (
                            <Center py={12}>
                                <Spinner color="#0071E3" />
                            </Center>
                        )}
                        {isError && (
                            <Text fontSize="sm" color="red.500" textAlign="center" py={8}>
                                Failed to load import history
                            </Text>
                        )}
                        {!isLoading && !isError && imports.length === 0 && (
                            <Text
                                fontSize="sm"
                                color="gray.400"
                                fontStyle="italic"
                                textAlign="center"
                                py={8}
                            >
                                No imports yet
                            </Text>
                        )}
                        {!isLoading && !isError && imports.length > 0 && (
                            <Flex direction="column" gap={2}>
                                {imports.map((entry) => (
                                    <Box
                                        key={entry.jobId}
                                        borderWidth="1px"
                                        borderColor="gray.200"
                                        borderRadius="lg"
                                        bg="white"
                                        p={3}
                                        cursor="pointer"
                                        _hover={{ borderColor: '#0071E3', bg: 'gray.50' }}
                                        onClick={() => onSelectJob(entry.jobId)}
                                    >
                                        <Flex align="center" justify="space-between" gap={2}>
                                            <Text
                                                fontSize="sm"
                                                fontWeight="semibold"
                                                color="gray.800"
                                            >
                                                {entry.createdCount} item
                                                {entry.createdCount === 1 ? '' : 's'} imported
                                            </Text>
                                            <Text fontSize="xs" color="gray.500" flexShrink={0}>
                                                {new Date(entry.createdAt).toLocaleString()}
                                            </Text>
                                        </Flex>
                                        <Text fontSize="xs" color="gray.500" mt={0.5} isTruncated>
                                            Job {entry.jobId}
                                        </Text>
                                        {entry.filename ? (
                                            <Text
                                                fontSize="xs"
                                                color="gray.500"
                                                mt={0.5}
                                                isTruncated
                                            >
                                                {entry.filename}
                                            </Text>
                                        ) : null}
                                    </Box>
                                ))}
                            </Flex>
                        )}
                    </Box>
                </Flex>
            </DrawerContent>
        </Drawer>
    );
};

export default InventoryImportHistoryDrawer;
